import { useEffect, useState } from "react";
import instance from "../../Interceptor/api_instance";
import "../../CSS/imageModal.css";
import { formatReadableDate } from "../../Helper/FormatDatetimes";
import { useTranslation } from "react-i18next";
import { usStates, mexicanStates } from "../../Helper/CountryStates";
import { SpinnerOverlay } from "../LoadingAnimations/SpinnerOverlay";
import TrailerSearchSelect from "../Inputs/TrailerSearch";
import TruckSearchSelect from "../Inputs/TruckSearch";
import { ActionResponse } from "./ActionResponses";

/**
 * The RowClickModal component renders a modal with options to view documents, edit row, and close the
 * modal.
 * @returns The `RowClickModal` component is being returned. It is a functional component that renders
 * a modal with information about a row, including buttons to set documents, edit the row, and close
 * the modal.
 */
export const EditVehicleModal = ({ row, close, english, type }) => {
  return (
    <div className="edit-modal">
      <form>
        {type === "trailer" ? (
          <>
            {row.repair_trailer?.map((repair) => (
              <div className="form-group">
                <label className="input-label">
                  {formatReadableDate(repair.created_at)}{" "}
                  {repair.employee.first_name} {repair.employee.last_name}
                </label>
                <textarea
                  className="text-input"
                  value={repair.notes}
                ></textarea>
              </div>
            ))}
          </>
        ) : null}
      </form>
      <div className="full-cont">
        <button className="change-pwd-btn">{english("buttons.submit")}</button>
        <button className="cancel-btn" onClick={close}>
          {english("buttons.back")}
        </button>
      </div>
    </div>
  );
};

export const VehicleRowClickModal = ({
  setDocuments,
  setEditRow,
  row,
  close,
  english,
  type,
}) => {
  return (
    <>
      <div className="edit-modal">
        <div>
          <h4 data-testid={"documents"} className="global-h2">
            {type.includes("trailer")
              ? row.trailer_number
              : row.carrier_name + "-" + row.truck_number}
          </h4>
          <div style={{ flexGrow: "1" }}></div>
        </div>
        <div style={{ margin: "auto" }}>
          <button className="other-btn gap" onClick={setDocuments}>
            {english("page-headers.documents")}
          </button>
          <button className="other-btn" onClick={setEditRow}>
            {english("buttons.edit-row")}
          </button>
        </div>
        <div className="btn-cont">
          <button onClick={close} className="close-btn">
            {english("buttons.close")}
          </button>
        </div>
      </div>
    </>
  );
};

export const NewRowClickModal = ({ viewImages, editRow, row, close }) => {
  const { t } = useTranslation();
  return (
    <div className="page-mask page-mask-animation">
      <div className="edit-modal">
        <div>
          <h4 data-testid={"documents"} className="global-h2">
            {t("yard-tables.table-columns.trailer")}:{" "}
            {row.trailer?.trailer_number}
          </h4>
          <h4 data-testid={"editRow"} className="global-h2">
            {t("yard-tables.table-columns.truck")}:{" "}
            {row.truck?.unit}
          </h4>
          <div style={{ flexGrow: "1" }}></div>
        </div>
        <div style={{ margin: "auto", display: "flex", gap: "8px" }}>
          <button className="other-btn gap" onClick={viewImages}>
            {t("page-headers.documents")}
          </button>
          <button className="other-btn" onClick={editRow}>
            {t("buttons.edit-row")}
          </button>
        </div>
        <div className="btn-cont">
          <button onClick={close} className="close-btn">
            {t("buttons.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export const RowClickModal = ({
  setDocuments,
  setEditRow,
  row,
  close,
  type,
}) => {
  const { t } = useTranslation();
  const getName = () => {
    switch (type) {
      case "trailers":
        return row.trailer.trailer_number;
      case "trucks":
        return row.truck.truckName;
      case "visitors":
        return row.visitor.driverName;
      case "truck-asset":
        return `${row.company}-${row.name}`;
      case "trailer-asset":
        return `${row.unit}`;
      default:
        break;
    }
  };
  return (
    <div className="page-mask page-mask-animation">
      <div className="edit-modal">
        <div>
          <h4 data-testid={"documents"} className="global-h2">
            {getName()}
          </h4>
          <div style={{ flexGrow: "1" }}></div>
        </div>
        <div style={{ margin: "auto", display: "flex", gap: "8px" }}>
          <button className="other-btn gap" onClick={setDocuments}>
            {t("page-headers.documents")}
          </button>
          {type !== "visitors" && (
            <button className="other-btn" onClick={setEditRow}>
              {t("buttons.edit-row")}
            </button>
          )}
        </div>
        <div className="btn-cont">
          <button onClick={close} className="close-btn">
            {t("buttons.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * The EditRowModal function in JavaScript handles editing and updating data for trailers and trucks,
 * including form inputs, dropdowns, and API calls.
 * @returns The `EditRowModal` component is being returned. It contains a form for editing either a
 * trailer or a truck based on the `type` prop passed to it. The form includes input fields for various
 * details such as customer, trailer/truck number, condition, origin, destination, and more. The form
 * also includes radio buttons, select dropdowns, and text inputs for user input.
 */

const getDestinationData = (destination) => {
  if (!destination) {
    return "";
  }
  const [dCity, dState, dCountry] = destination.split(", ");
  return [dCity, dState, dCountry];
};

const getOriginData = (origin) => {
  if (!origin) {
    return "";
  }
  const [city, state, country] = origin.split(", ");
  return [city, state, country];
};

export const EditTrailerRow = ({ close, row }) => {
  const { t } = useTranslation();
  const trailer = row.trailer;
  console.log(row);

  // Condition states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitResponse, setSubmitResponse] = useState(null);
  const [isAti, setIsAti] = useState(row.trailer.trailer_asset ? true : false);

  // Split origin and destination into separate fields
  const [city, state, country] = getOriginData(row.trailer.origin);
  const [dCity, dState, dCountry] = getDestinationData(row.trailer.destination);

  // Set original data with split fields
  const [trailerData, setTrailerData] = useState({
    id: row.id,
    trailerPlate: trailer.license_plate,
    trailerDimensions: trailer.trailer_dimensions,
    isLoaded: trailer.is_trailer_loaded,
    customer: trailer.customer,
    sealNumber: trailer.sealNumber,
    checkOutSealNumber: trailer.check_out_seal_number,
    trailerCondition: trailer.trailerCondition,
    trailerConditionNotes: trailer.trailer_condition_notes,
    trailerAsset: trailer.trailer_asset,
    trailerOption: trailer.trailer_asset
      ? { label: trailer.trailer_number, value: trailer.trailer_asset.id }
      : null,
    city: city,
    state: state,
    country: country,
    dCity: dCity,
    dState: dState,
    dCountry: dCountry,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTrailerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Patch input changes to API
  const postEditChanges = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      const data = { ...trailerData };
      const response = await instance.patch("assets/edit-trailer", data);

      if (response.status === 200) {
        setSubmitResponse({
          status: t("forms.type.success"),
          message: t("forms.responses.success"),
        });
      }
    } catch (error) {
      setSubmitResponse({
        status: "Error",
        message: error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle selection in trailer list
  const handleTrailerSelect = (selectedOption) => {
    if (!selectedOption) {
    }
    setTrailerData((prevState) => ({
      ...prevState,
      trailerAsset: selectedOption.id,
      trailerPlate: selectedOption.licensePlate,
      trailerOption: selectedOption,
    }));
  };

  return (
    <div className="page-mask page-mask-animation">
      {isLoading && <SpinnerOverlay />}
      {submitResponse && (
        <ActionResponse
          message={submitResponse.message}
          status={submitResponse.status}
          close={
            submitResponse.status === t("forms.type.success")
              ? () => window.location.reload()
              : () => setSubmitResponse(null)
          }
        />
      )}
      {error && <p className="error">{error}</p>}
      <div className="edit-modal">
        <div>
          <h4 className="global-h2">{t("forms.type.edit-entry")}</h4>
          <p className="gray">{trailerData.trailer_number}</p>
          <form onSubmit={(e) => postEditChanges(e)}>
            {/* Form group: Is trailer ATI */}
            <div className="form-group">
              <label className="input-label">
                {t("forms.type.vehicle.input-headers.ati-bool")}{" "}
              </label>
              <div className="input-cont">
                <fieldset className="fieldsets">
                  <div className="radio-options">
                    <input
                      type="radio"
                      name="group1"
                      className="radio-input"
                      checked={isAti}
                      onChange={() => {
                        setIsAti(true);
                        handleTrailerSelect({
                          value: null,
                          license_plate: null,
                        });
                      }}
                      required
                    />
                    <label>{t("general.yes")}</label>
                  </div>
                  <div className="radio-options">
                    <input
                      type="radio"
                      name="group1"
                      className="radio-input"
                      checked={isAti === false}
                      onChange={() => {
                        setIsAti(false);
                        handleTrailerSelect({
                          value: null,
                          license_plate: null,
                        });
                      }}
                    />
                    <label>No</label>
                  </div>
                </fieldset>
              </div>
            </div>
            {/* Form group: Trailer number */}
            <div className="form-group">
              {isAti ? (
                <>
                  <label className="input-label">
                    {t("forms.type.vehicle.placeholders.select-trailer")}
                  </label>
                  <div className="input-cont">
                    <TrailerSearchSelect
                      onSelect={handleTrailerSelect}
                      selectedTrailer={trailerData.trailerOption}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="trailerNum" className="input-label">
                      {t("forms.type.vehicle.input-headers.trailer-number")}
                    </label>
                    <div className="input-cont">
                      <input
                        type="text"
                        value={trailerData.trailer_number}
                        className="text-input"
                        name="trailer_number"
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Form group: license plate */}
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.table-columns.license_plate")}
              </label>
              <div className="input-cont">
                <input
                  type="text"
                  className="text-input"
                  name="license_plate"
                  value={trailerData.license_plate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            {/* Form group: is loaded */}
            <div className="form-group">
              <div className="form-group">
                <label className="input-label">
                  {t("yard-tables.search-filters.dropdowns.contents")}
                </label>
                <div className="input-cont">
                  <select
                    data-testid="is_loaded"
                    onChange={handleInputChange}
                    value={trailerData.is_loaded}
                    name="is_loaded"
                  >
                    <option value={true}>
                      {t("forms.type.vehicle.placeholders.loaded")}
                    </option>
                    <option value={false}>
                      {t("forms.type.vehicle.placeholders.empty")}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            {/* Form group: customer */}
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.search-filters.dropdowns.customer")}
              </label>
              <div className="input-cont">
                <input
                  type="text"
                  name="customer"
                  className="text-input"
                  value={trailerData.customer}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            {/* Form group: seal number */}
            <div className="form-group">
              <label className="input-label">
                {t("forms.type.vehicle.input-headers.sealNum")}
              </label>
              <div className="input-cont">
                <input
                  onChange={handleInputChange}
                  value={trailerData.seal_number}
                  name="seal_number"
                  data-testid="seal_number"
                  className="text-input"
                  placeholder={t("forms.type.vehicle.placeholders.num")}
                />
              </div>
            </div>
            {/* Form group: exit seal number */}
            <div className="form-group">
              <label className="input-label">
                {t("forms.type.vehicle.input-headers.exitSealNum")}
              </label>
              <div className="input-cont">
                <input
                  onChange={handleInputChange}
                  data-testid="check_out_seal_number"
                  value={trailerData.check_out_seal_number}
                  name="check_out_seal_number"
                  className="text-input"
                  placeholder={t("forms.type.vehicle.placeholders.num")}
                />
              </div>
            </div>
            {/* Form group: Condition */}
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.table-columns.condition")}
              </label>
              <div className="input-cont">
                <div>
                  <select
                    id="conditionSelect"
                    onChange={handleInputChange}
                    value={trailerData.trailer_condition}
                    name="trailer_condition"
                    data-testid="trailer_condition"
                    required
                  >
                    <option value="Good">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.good"
                      )}
                    </option>
                    <option value="Okay">Okay</option>
                    <option value="Poor">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.poor"
                      )}
                    </option>
                    <option value="Critical">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.critical"
                      )}
                    </option>
                    <option value="Under Repair">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.under-repair"
                      )}
                    </option>
                    <option value="Waiting parts">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.spare-parts"
                      )}
                    </option>
                    <option value="Waiting instructions">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.instructions"
                      )}
                    </option>
                    <option value="External repair">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.external-repair"
                      )}
                    </option>
                  </select>
                  <textarea
                    type="text"
                    className="text-input"
                    placeholder={t("forms.type.vehicle.placeholders.notes")}
                    value={trailerData.trailer_condition_notes}
                    name="trailer_condition_notes"
                    onChange={handleInputChange}
                    required={
                      trailerData.trailer_condition !== "Good" &&
                      trailerData.trailer_condition !== "Okay"
                        ? true
                        : false
                    }
                  />
                </div>
              </div>
            </div>
            {/* Form group: Origin */}
            <div className="form-group">
              <label className="input-label">
                {t("forms.type.vehicle.input-headers.origin")}
              </label>
              <div className="input-cont">
                <select
                  id="country"
                  name="country"
                  data-testid="country"
                  onChange={handleInputChange}
                  value={trailerData.country}
                  required
                >
                  <option value="USA">
                    {t("forms.type.vehicle.placeholders.usa")}
                  </option>
                  <option value="Mexico">Mexico</option>
                </select>
                <select
                  id="state"
                  name="state"
                  data-testid="state"
                  value={trailerData.state}
                  onChange={handleInputChange}
                  required
                >
                  {trailerData.country === "USA" &&
                    usStates.map((state, index) => (
                      <option key={`${state}-${index}`} value={state}>
                        {state}
                      </option>
                    ))}
                  {trailerData.country === "Mexico" &&
                    mexicanStates.map((state, index) => (
                      <option key={`${state}-${index}`} value={state}>
                        {state}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="input-label"></label>
              <div className="input-cont">
                <input
                  id="city"
                  type="text"
                  name="city"
                  date-testid="city"
                  value={trailerData.city}
                  className="text-input"
                  placeholder={t(
                    "forms.type.vehicle.placeholders.destination.City"
                  )}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            {/* Form group: Destination */}
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.search-filters.dropdowns.destination")}
              </label>
              <div className="input-cont">
                <select
                  id="dCountry"
                  name="dCountry"
                  data-testid="dCountry"
                  onChange={handleInputChange}
                  value={trailerData.dCountry}
                >
                  <option value="" selected disabled>
                    {t("forms.type.vehicle.placeholders.destination.country")}
                  </option>
                  <option value="USA">
                    {t("forms.type.vehicle.placeholders.usa")}
                  </option>
                  <option value="Mexico">Mexico</option>
                </select>
                <select
                  id="state"
                  name="state"
                  data-testid="state"
                  value={trailerData.dState}
                  onChange={handleInputChange}
                >
                  <option value="" selected disabled>
                    {t("forms.type.vehicle.placeholders.destination.state")}
                  </option>
                  {trailerData.country === "USA" &&
                    usStates.map((state, index) => (
                      <option key={`${state}-${index}`} value={state}>
                        {state}
                      </option>
                    ))}
                  {trailerData.country === "Mexico" &&
                    mexicanStates.map((state, index) => (
                      <option key={`${state}-${index}`} value={state}>
                        {state}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="input-label"></label>
              <div className="input-cont">
                <input
                  id="city"
                  type="text"
                  name="city"
                  date-testid="city"
                  className="text-input"
                  value={trailerData.dCity}
                  placeholder={t(
                    "forms.type.vehicle.placeholders.destination.City"
                  )}
                />
              </div>
            </div>
            <div className="full-cont">
              <button type="submit" className="other-btn">
                {t("buttons.update")}
              </button>
              <button onClick={close} className="close-btn">
                {t("buttons.close")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const EditTruckRow = ({ close, row }) => {
  const { t } = useTranslation();

  // Condition states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitResponse, setSubmitResponse] = useState(null);
  const [truckList, setTruckList] = useState({ ati: [], jfg: [] });
  const [otherCarrier, setOtherCarrier] = useState("");

  // Split origin and destination into separate fields
  const [city, state, country] = getOriginData(row.truck.origin);
  const [dCity, dState, dCountry] = getDestinationData(row.truck.destination);

  // Set original data with split fields

  const [truckData, setTruckData] = useState({
    ...row.truck,
    city: city,
    state: state,
    country: country,
    dCity: dCity,
    dState: dState,
    dCountry: dCountry,
  });

  // Select dropdown variable
  // const atiTruckOptions = truckList.ati.map((truck) => ({
  //   ...truck,
  //   value: truck.id,
  //   label: `${truck.company}-${truck.name} ${
  //     truck.location ? truck.location.loc_name : ""
  //   }`,
  //   isDisabled:
  //     truck.location && row.truck.truck_asset?.id !== truck.id ? true : false,
  // }));

  // const jfgTruckOptions = truckList.jfg.map((truck) => ({
  //   ...truck,
  //   value: truck.id,
  //   label: `${truck.company}-${truck.name} ${
  //     truck.location ? truck.location.loc_name : ""
  //   }`,
  //   isDisabled:
  //     truck.location && row.truck.truck_asset?.id !== truck.id ? true : false,
  // }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTruckData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Patch input changes to API
  const postEditChanges = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      const data = {
        ...truckData,
        // Assign other carrier name
        carrier_name:
          truckData.carrier_name === "Other"
            ? otherCarrier
            : truckData.carrier_name,
        // Keep or remove truck_asset based on carrier name
        truck_asset:
          truckData.carrier_name === "Other" ? null : truckData.truck_asset,
      };
      const response = await instance.patch("assets/edit-truck", data);

      if (response.status === 200) {
        setSubmitResponse({
          status: t("forms.type.success"),
          message: t("forms.responses.success"),
        });
      }
    } catch (error) {
      setSubmitResponse({
        status: "Error",
        message: error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle selection in truck list
  const handleTruckSelect = (selectedOption) => {
    setTruckData((prevState) => ({
      ...prevState,
      truck_asset: selectedOption,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await instance.get("assets/get-trucks-out");
        if (response.status === 200) {
          setTruckList(response.data);
        }
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    if (["JFG", "ATI"].includes(row.truck.carrier_name)) {
      setTruckData((prev) => ({
        ...prev,
        carrier_name: row.truck.carrier_name,
      }));
    } else {
      setTruckData((prev) => ({
        ...prev,
        carrier_name: "Other",
      }));
      setOtherCarrier(row.truck.carrier_name);
    }
  }, []);

  return (
    <div className="page-mask page-mask-animation">
      {isLoading && <SpinnerOverlay />}
      {submitResponse && (
        <ActionResponse
          message={submitResponse.message}
          status={submitResponse.status}
          close={
            submitResponse.status === t("forms.type.success")
              ? () => window.location.reload()
              : () => setSubmitResponse(null)
          }
        />
      )}
      {error && <p className="error">{error}</p>}
      <div className="edit-modal">
        <div>
          <h4 className="global-h2">{t("forms.type.edit-entry")}</h4>
          <p className="gray">
            {truckData.carrier_name}-{truckData.truck_number}
          </p>
          <form onSubmit={(e) => postEditChanges(e)}>
            {/* Form group: Carrier */}
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.table-columns.carrier")}
              </label>
              <div className="input-cont">
                <select
                  name="carrier_name"
                  onChange={handleInputChange}
                  value={truckData.carrier_name}
                  required
                >
                  <option value="JFG">JFG</option>
                  <option value="ATI">ATI</option>
                  <option value="Other">
                    {t("forms.type.vehicle.placeholders.other")}
                  </option>
                </select>
                {truckData.carrier_name === "Other" && (
                  <input
                    type="text"
                    className="text-input"
                    value={otherCarrier}
                    onChange={(e) => setOtherCarrier(e.target.value)}
                    required
                  />
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.table-columns.truck")}
              </label>
              <div className="input-cont">
                {truckData.carrier_name === "JFG" ? (
                  // <Select
                  //   options={jfgTruckOptions}
                  //   styles={customStyles}
                  //   unstyled={true}
                  //   isSearchable={true}
                  //   isClearable={true}
                  //   value={jfgTruckOptions.find(
                  //     (truck) => truck.value === truckData.truck_asset?.id
                  //   )}
                  //   onChange={handleTruckSelect}
                  //   required={true}
                  // />
                  <TruckSearchSelect
                    onSelect={handleTruckSelect}
                    carrier={truckData.carrier_name}
                  />
                ) : truckData.carrier_name === "ATI" ? (
                  // <Select
                  //   options={atiTruckOptions}
                  //   styles={customStyles}
                  //   unstyled={true}
                  //   isSearchable={true}
                  //   isClearable={true}
                  //   value={atiTruckOptions.find(
                  //     (truck) => truck.value === truckData.truck_asset?.id
                  //   )}
                  //   onChange={handleTruckSelect}
                  //   required={true}
                  // />
                  <TruckSearchSelect
                    onSelect={handleTruckSelect}
                    carrier={truckData.carrier_name}
                  />
                ) : (
                  <input
                    type="text"
                    className="text-input"
                    name="truck_number"
                    value={truckData.truck_number}
                    onChange={handleInputChange}
                    required
                  />
                )}
              </div>
            </div>
            {/* Form group: license plate */}
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.table-columns.license_plate")}
              </label>
              <div className="input-cont">
                <input
                  type="text"
                  className="text-input"
                  name="truck_plate"
                  value={truckData.truck_plate}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            {/* Form group: Condition */}
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.table-columns.condition")}
              </label>
              <div className="input-cont">
                <div>
                  <select
                    id="conditionSelect"
                    name="truck_condition"
                    value={truckData.truck_condition}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Good">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.good"
                      )}
                    </option>
                    <option value="Okay">Okay</option>
                    <option value="Poor">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.poor"
                      )}
                    </option>
                    <option value="Critical">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.critical"
                      )}
                    </option>
                    <option value="Under Repair">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.under-repair"
                      )}
                    </option>
                    <option value="Waiting parts">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.spare-parts"
                      )}
                    </option>
                    <option value="Waiting instructions">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.instructions"
                      )}
                    </option>
                    <option value="External repair">
                      {t(
                        "forms.type.vehicle.placeholders.select-condition.external-repair"
                      )}
                    </option>
                  </select>
                  <textarea
                    type="text"
                    className="text-input"
                    placeholder={t("forms.type.vehicle.placeholders.notes")}
                    value={truckData.truck_condition_notes}
                    name="truck_condition_notes"
                    onChange={handleInputChange}
                    required={
                      truckData.truck_condition !== "Good" &&
                      truckData.truck_condition !== "Okay"
                        ? true
                        : false
                    }
                  ></textarea>
                </div>
              </div>
            </div>
            {/* Form group: Origin */}
            <div className="form-group">
              <label className="input-label">
                {t("forms.type.vehicle.input-headers.origin")}
              </label>
              <div className="input-cont">
                <select
                  id="country"
                  name="country"
                  data-testid="country"
                  onChange={handleInputChange}
                  value={truckData.country}
                  required
                >
                  <option value="USA">
                    {t("forms.type.vehicle.placeholders.usa")}
                  </option>
                  <option value="Mexico">Mexico</option>
                </select>
                <select
                  id="state"
                  name="state"
                  data-testid="state"
                  value={truckData.state}
                  onChange={handleInputChange}
                  required
                >
                  {truckData.country === "USA" &&
                    usStates.map((state, index) => (
                      <option key={`${state}-${index}`} value={state}>
                        {state}
                      </option>
                    ))}
                  {truckData.country === "Mexico" &&
                    mexicanStates.map((state, index) => (
                      <option key={`${state}-${index}`} value={state}>
                        {state}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="input-label"></label>
              <div className="input-cont">
                <input
                  id="city"
                  type="text"
                  name="city"
                  date-testid="city"
                  value={truckData.city}
                  className="text-input"
                  placeholder={t(
                    "forms.type.vehicle.placeholders.destination.City"
                  )}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            {/* Form group: Destination */}
            <div className="form-group">
              <label className="input-label">
                {t("yard-tables.search-filters.dropdowns.destination")}
              </label>
              <div className="input-cont">
                <select
                  id="dCountry"
                  name="dCountry"
                  data-testid="dCountry"
                  onChange={handleInputChange}
                  value={truckData.dCountry}
                >
                  <option value="" selected disabled>
                    {t("forms.type.vehicle.placeholders.destination.country")}
                  </option>
                  <option value="USA">
                    {t("forms.type.vehicle.placeholders.usa")}
                  </option>
                  <option value="Mexico">Mexico</option>
                </select>
                <select
                  id="state"
                  name="state"
                  data-testid="state"
                  value={truckData.dState}
                  onChange={handleInputChange}
                >
                  <option value="" selected disabled>
                    {t("forms.type.vehicle.placeholders.destination.state")}
                  </option>
                  {truckData.country === "USA" &&
                    usStates.map((state, index) => (
                      <option key={`${state}-${index}`} value={state}>
                        {state}
                      </option>
                    ))}
                  {truckData.country === "Mexico" &&
                    mexicanStates.map((state, index) => (
                      <option key={`${state}-${index}`} value={state}>
                        {state}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="input-label"></label>
              <div className="input-cont">
                <input
                  id="city"
                  type="text"
                  name="city"
                  date-testid="city"
                  className="text-input"
                  value={truckData.dCity}
                  placeholder={t(
                    "forms.type.vehicle.placeholders.destination.City"
                  )}
                />
              </div>
            </div>
            <div className="full-cont">
              <button type="submit" className="other-btn">
                {t("buttons.update")}
              </button>
              <button onClick={close} className="close-btn">
                {t("buttons.close")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
